import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchDashboardSummary, fetchAccomplishmentStats, fetchProjectStats } from '../../services/dashboardService';
import FollowupQueue from '../../components/dashboard/FollowupQueue';
import SubscriptionManagement from '../../components/dashboard/SubscriptionManagement';
import UsageMeters from '../../components/dashboard/UsageMeters';
import { TrialBanner } from '../../components/dashboard/TrialBanner';
import OnboardingModal from '../../components/dashboard/OnboardingModal';
import MotivationModal from '../../components/dashboard/MotivationModal';
import PersonaSelector from '../../components/dashboard/PersonaSelector';
import { 
  BookOpen, 
  Users, 
  MessageSquare, 
  TrendingUp, 
  LogOut, 
  Download,
  Mail,
  Clock,
  Sparkles,
  ChevronRight,
  Loader2,
  Trophy,
  Settings,
  FolderKanban,
  FileText
} from 'lucide-react';

const DashboardPage = () => {
  const { user, logout, isAuthenticated, loading: authLoading, trialStatus, trialDaysRemaining, isTrialReadOnly, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [accStats, setAccStats] = useState(null);
  const [projectStats, setProjectStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showMotivation, setShowMotivation] = useState(false);
  const [showPersona, setShowPersona] = useState(false);
  // Track if user has dismissed modals this session (to prevent re-showing after skip)
  const [motivationDismissed, setMotivationDismissed] = useState(false);
  const [personaDismissed, setPersonaDismissed] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [data, stats, projStats] = await Promise.all([
          fetchDashboardSummary(),
          fetchAccomplishmentStats().catch(() => null),
          fetchProjectStats().catch(() => null),
        ]);
        setDashboardData(data);
        setAccStats(stats);
        setProjectStats(projStats);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadDashboard();
    }
  }, [isAuthenticated, navigate]);

  // Show onboarding modals in sequence: Profile → Persona → Motivation
  // Only show each modal once per session (respect user's skip decision)
  useEffect(() => {
    if (user && !loading) {
      if (!user.onboarding_complete) {
        setShowOnboarding(true);
      } else if (!user.persona_id && !showOnboarding && !personaDismissed) {
        setShowPersona(true);
      } else if (!user.motivation && !showOnboarding && !showPersona && !motivationDismissed) {
        setShowMotivation(true);
      }
    }
  }, [user, loading, showOnboarding, showPersona, personaDismissed, motivationDismissed]);

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    await refreshUser();
    setShowPersona(true);
  };

  const handlePersonaComplete = async () => {
    setShowPersona(false);
    setPersonaDismissed(true); // Mark as handled this session
    await refreshUser();
    setShowMotivation(true);
  };

  const handleMotivationComplete = async () => {
    setShowMotivation(false);
    setMotivationDismissed(true); // Mark as handled this session
    await refreshUser();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingComplete}
        />
      )}

      {/* Persona Selector Modal */}
      {showPersona && (
        <PersonaSelector
          currentPersonaId={user?.persona_id}
          onComplete={handlePersonaComplete}
          onSkip={handlePersonaComplete}
        />
      )}

      {/* Motivation Modal */}
      {showMotivation && (
        <MotivationModal
          onComplete={handleMotivationComplete}
          onSkip={handleMotivationComplete}
        />
      )}

      {/* Trial Banner - shows at top when user is on trial */}
      <TrialBanner 
        trialStatus={trialStatus} 
        daysRemaining={trialDaysRemaining} 
        isReadOnly={isTrialReadOnly} 
      />
      
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/it-leadership" className="text-xl font-bold text-white">
              30 Day Skills
            </Link>
            <span className="text-zinc-600">|</span>
            <span className="text-zinc-400">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm hidden sm:block">
              {user?.email}
            </span>
            <Link
              to="/dashboard/settings"
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="settings-link"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Welcome back{dashboardData?.user_name ? `, ${dashboardData.user_name}` : ''}
          </h1>
          <p className="text-zinc-400">
            {user?.job_title && user?.company_name
              ? `${user.job_title} at ${user.company_name}`
              : user?.job_title
                ? user.job_title
                : user?.company_name
                  ? user.company_name
                  : "Here's an overview of your 30 Day Skills journey"}
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Books owned */}
          <StatCard
            icon={<BookOpen className="w-5 h-5" />}
            label="Books Owned"
            value={dashboardData?.purchases?.books?.length || 0}
            color="red"
          />
          
          {/* AI Access */}
          <StatCard
            icon={<Sparkles className="w-5 h-5" />}
            label="AI Companion"
            value={dashboardData?.companion_enabled ? (
              dashboardData?.purchases?.ai_access_type === 'purchased' ? 'Active' : 'Trial'
            ) : 'Not Active'}
            color={dashboardData?.companion_enabled ? 'green' : 'zinc'}
          />
          
          {/* People mentioned */}
          <StatCard
            icon={<Users className="w-5 h-5" />}
            label="People Mentioned"
            value={dashboardData?.people_count || 0}
            color="blue"
            href="/dashboard/people"
          />
          
          {/* Reflections */}
          <StatCard
            icon={<MessageSquare className="w-5 h-5" />}
            label="Total Reflections"
            value={dashboardData?.companion_stats?.total_reflections || 0}
            color="purple"
          />
        </div>

        {/* Usage Meters - Tier and limits visibility */}
        {dashboardData?.companion_enabled && (
          <div className="mb-6">
            <UsageMeters />
          </div>
        )}

        {/* Accomplishments banner - show if there are pending suggestions */}
        {dashboardData?.companion_enabled && accStats?.pending_suggestions > 0 && (
          <Link
            to="/dashboard/accomplishments"
            className="mb-8 flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl hover:border-amber-500/40 transition-colors"
            data-testid="accomplishments-banner"
          >
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Trophy className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                {accStats.pending_suggestions} new accomplishment{accStats.pending_suggestions !== 1 ? 's' : ''} detected
              </p>
              <p className="text-zinc-400 text-xs">
                We spotted achievements in your reflections — review and confirm them
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-500/60 flex-shrink-0" />
          </Link>
        )}

        {/* Accomplishments link - show when user has confirmed accomplishments */}
        {dashboardData?.companion_enabled && accStats?.confirmed_accomplishments > 0 && accStats?.pending_suggestions === 0 && (
          <Link
            to="/dashboard/accomplishments"
            className="mb-8 flex items-center gap-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors"
            data-testid="accomplishments-link"
          >
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Trophy className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                {accStats.confirmed_accomplishments} accomplishment{accStats.confirmed_accomplishments !== 1 ? 's' : ''} tracked
              </p>
              <p className="text-zinc-400 text-xs">
                View your achievement record
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
          </Link>
        )}

        {/* Projects link - show when user has projects */}
        {dashboardData?.companion_enabled && projectStats?.total_projects > 0 && (
          <Link
            to="/dashboard/projects"
            className="mb-8 flex items-center gap-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-colors"
            data-testid="projects-link"
          >
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FolderKanban className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                {projectStats.active_projects} active project{projectStats.active_projects !== 1 ? 's' : ''}
              </p>
              <p className="text-zinc-400 text-xs">
                {projectStats.total_projects} total tracked • {projectStats.total_people_across_projects} team members
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
          </Link>
        )}

        {/* Follow-up Queue - shows pending follow-ups */}
        {dashboardData?.companion_enabled && (
          <div className="mb-8">
            <FollowupQueue />
          </div>
        )}

        {/* Living CV link - always show for companion users */}
        {dashboardData?.companion_enabled && (
          <Link
            to="/dashboard/living-cv"
            className="mb-4 flex items-center gap-4 p-4 bg-gradient-to-r from-zinc-900 to-zinc-900/80 border border-zinc-800 rounded-xl hover:border-red-500/30 transition-colors group"
            data-testid="living-cv-link"
          >
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/20 transition-colors">
              <FileText className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                Your Living CV
              </p>
              <p className="text-zinc-400 text-xs">
                Leadership evidence from your reflections
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0 group-hover:text-red-400 transition-colors" />
          </Link>
        )}

        {/* Situations link - for users tracking work challenges */}
        {dashboardData?.companion_enabled && (
          <Link
            to="/dashboard/situations"
            className="mb-8 flex items-center gap-4 p-4 bg-gradient-to-r from-zinc-900 to-zinc-900/80 border border-zinc-800 rounded-xl hover:border-amber-500/30 transition-colors group"
            data-testid="situations-link"
          >
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/20 transition-colors">
              <MessageSquare className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                My Situations
              </p>
              <p className="text-zinc-400 text-xs">
                Track work challenges and see patterns emerge
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0 group-hover:text-amber-400 transition-colors" />
          </Link>
        )}

        {/* Performance Review link */}
        {dashboardData?.companion_enabled && (
          <Link
            to="/dashboard/review"
            className="mb-8 flex items-center gap-4 p-4 bg-gradient-to-r from-zinc-900 to-zinc-900/80 border border-zinc-800 rounded-xl hover:border-green-500/30 transition-colors group"
            data-testid="review-link"
          >
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20 transition-colors">
              <FileText className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">
                Performance Review
              </p>
              <p className="text-zinc-400 text-xs">
                Generate your quarterly summary for reviews
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0 group-hover:text-green-400 transition-colors" />
          </Link>
        )}

        {/* Main sections */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Books section */}
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-red-500" />
                Your Books
              </h2>
            </div>
            
            {dashboardData?.purchases?.books?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.purchases.books.map((book) => (
                  <div
                    key={book.id}
                    className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg"
                  >
                    {book.cover_image ? (
                      <img
                        src={book.cover_image}
                        alt={book.title}
                        className="w-12 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-16 bg-zinc-700 rounded flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-zinc-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{book.title}</h3>
                      {book.subtitle && (
                        <p className="text-zinc-400 text-sm truncate">{book.subtitle}</p>
                      )}
                    </div>
                    {book.download_token && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/download/${book.download_token}`}
                        className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-zinc-300" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-4">No books purchased yet</p>
                <Link
                  to="/it-leadership#pricing"
                  className="inline-block px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Browse Books
                </Link>
              </div>
            )}
          </section>

          {/* AI Companion section */}
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 sm:p-6 overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="truncate">Companion</span>
              </h2>
            </div>

            {dashboardData?.companion_enabled ? (
              <div className="space-y-4">
                {/* Weekly Usage - show unlimited banner during promo period */}
                {dashboardData.companion_stats.limits_disabled ? (
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-3 sm:p-4">
                    <div className="flex items-start sm:items-center gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-green-400 font-semibold text-sm">🎉 Unlimited Reflections</p>
                        <p className="text-zinc-400 text-xs">
                          No weekly limits until Feb 28 — reflect as often as you like!
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-sm">This week&apos;s reflections</span>
                      <span className="text-white font-medium">
                        {dashboardData?.companion_stats?.weekly_reflections || 0} / {dashboardData?.companion_stats?.weekly_limit || 20}
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, ((dashboardData?.companion_stats?.weekly_reflections || 0) / (dashboardData?.companion_stats?.weekly_limit || 20)) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* How to use */}
                <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium mb-1 text-sm sm:text-base">Email your reflections</p>
                      <p className="text-zinc-400 text-xs sm:text-sm break-words">
                        Send your thoughts to <span className="text-purple-400 break-all">coach@30dayskills.co.uk</span> and receive AI-powered guidance
                      </p>
                    </div>
                  </div>
                </div>

                {/* Patterns detected */}
                {dashboardData?.top_patterns?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-zinc-400 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Detected Patterns
                    </h3>
                    <div className="space-y-2">
                      {dashboardData.top_patterns.slice(0, 2).map((pattern, i) => (
                        <div key={i} className="p-3 bg-zinc-800/50 rounded-lg text-sm text-zinc-300">
                          {pattern.insight}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 mb-2">AI Companion not active</p>
                <p className="text-zinc-500 text-sm mb-4">
                  Get personalized management coaching via email
                </p>
                <Link
                  to="/it-leadership#pricing"
                  className="inline-block px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Get AI Companion
                </Link>
              </div>
            )}
          </section>

          {/* Recent reflections */}
          {dashboardData?.companion_enabled && dashboardData?.recent_reflections?.length > 0 && (
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  Recent Reflections
                </h2>
                <Link to="/dashboard/reflections" className="text-sm text-zinc-400 hover:text-white transition-colors">
                  View all →
                </Link>
              </div>
              
              <div className="space-y-3">
                {dashboardData.recent_reflections.map((reflection) => (
                  <Link
                    key={reflection.conversation_id}
                    to={`/dashboard/reflections/${reflection.conversation_id}`}
                    className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">
                        {reflection.preview || 'Reflection'}
                      </p>
                      <p className="text-zinc-500 text-sm">
                        {new Date(reflection.created_at).toLocaleDateString()} • {reflection.message_count} messages
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-600" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* People context */}
          {dashboardData?.companion_enabled && dashboardData?.people_count > 0 && (
            <Link 
              to="/dashboard/people"
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 lg:col-span-2 hover:border-zinc-700 transition-colors block"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-500" />
                  Your People Context
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">
                    {dashboardData.people_count} people mentioned
                  </span>
                  <ChevronRight className="w-5 h-5 text-zinc-600" />
                </div>
              </div>
              <p className="text-zinc-400 text-sm">
                The Companion remembers the people you discuss in your reflections, 
                helping provide more personalized guidance over time.
              </p>
            </Link>
          )}

          {/* Subscription Management - only show for users with AI access */}
          {dashboardData?.purchases?.has_ai_access && dashboardData?.purchases?.ai_access_type === 'purchased' && (
            <div className="lg:col-span-2">
              <SubscriptionManagement userEmail={user?.email} />
            </div>
          )}

          {/* Quick Actions */}
          {dashboardData?.companion_enabled && (
            <section className="lg:col-span-2">
              <Link
                to="/dashboard/chat"
                className="flex items-center justify-center gap-3 w-full py-5 bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 text-white font-medium rounded-xl transition-all"
                data-testid="start-chat-button"
              >
                <MessageSquare className="w-5 h-5" />
                Start New Reflection
              </Link>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

// Stat card component
const StatCard = ({ icon, label, value, color = 'zinc', href = null }) => {
  const colorClasses = {
    red: 'bg-red-500/10 text-red-500',
    green: 'bg-green-500/10 text-green-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
    zinc: 'bg-zinc-800 text-zinc-400'
  };

  const content = (
    <>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-zinc-400 text-sm">{label}</p>
    </>
  );

  if (href) {
    return (
      <Link 
        to={href} 
        className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors block"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      {content}
    </div>
  );
};

export default DashboardPage;
